/**
 * Api handlers for the App.
 * These will expose functions using the endpoints to call the survey api
 */
// import { endpoints } from './constants.js';
import { del, get, post, put, fetchOptions } from '@itslearning/atlas/network/fetch';
import { get as getStoreObj } from 'svelte/store';
import { store } from './stores';

/**
* Get the base url to the api, surveyId and surveyInstanceId values are retrieved from the store
*/
function getBaseUrl() {
    const s = getStoreObj(store);
    const apiBaseUrl = s.apiBaseUrl;

    return `${apiBaseUrl}/${s.surveyId}/instances/${s.surveyInstanceId}`;
}

/*
* Gets base options for api requests
*/
function getBaseOptions() {
    return {
        mode: 'cors',
        credentials: 'include',
        headers: { 'x-its-csrf': '1', 'x-its-target': 'serpent' }
    };
}

/**
 * Update survey
 *
 * @export
 * @param {String} introduction the introduction text for the survey
 * @return {*}
 */
export function saveSurvey(introduction, respondentType) { // TODO this must be changed a bit...
    return post(
        getBaseUrl(),
        {
            description: introduction,
            respondentType: respondentType
        },
        getBaseOptions()
    );
}

/**
 * Update survey
 *
 * @export
 * @param {String} description text for the AI to generate survey from (will be used as part of AI prompt)
 * @param {Number} numberOfQuestions numberOfQuestions to generate
 * @param {Number} respondentType 1, 2 or 3 (Not anonymous, anonymous and open extern anonymous)
 * @return {*}
 */
export function createSurveyAI(description, numberOfQuestions, respondentType) {
    return post(
        `${getBaseUrl()}/ai`,
        {
            description: description,
            numberOfQuestions: numberOfQuestions,
            respondentType: respondentType,
        },
        getBaseOptions()
    );
}

/**
 * Create answer - should only be called if the user does not already have an answer
 * @returns
 */
export function createAnswer() {
    return post(
        `${getBaseUrl()}/answers`,
        {
        },
        getBaseOptions()
    );
}

/**
 *
 * @param {string} measurement
 */
export function addKpi(measurement) {
    return post(
        `${getBaseUrl()}/kpi`,
        { measurement: measurement },
        getBaseOptions()
    );
}

/**
 * Add/update question
 *
 * @export
 * @param {Number} surveyId an integer
 * @param {Number} surveyInstanceId an integer
 * @param {object} question
 * @return {*}
 */
export function saveQuestion(question) {
    return post(
        `${getBaseUrl()}/questions`,
        { questions: [question] },
        getBaseOptions()
    );
}

/**
 * set question order (sorting)
 *
 * @export
 * @param {Number} questionId
 * @param {Number} orderNo
 * @return {*}
 */
export function saveQuestionOrder(questionId, orderNo) {
    return put(
        `${getBaseUrl()}/questions/${questionId}`,
        { orderNo: orderNo },
        getBaseOptions()
    );
}

export function deleteQuestions(questionIds) {
    return del(
        `${getBaseUrl()}/questions?questionIds=${questionIds.join(',')}`,
        {},
        getBaseOptions()
    );
}

/**
 * Save question answer
 *
 * @export
 * @param {Number} answerId an integer
 * @param {object} questionAnswer
 * @return {*}
 */
export function saveQuestionAnswer(answerId, questionAnswer, isCompleted = false) {
    return post(
        `${getBaseUrl()}/answers/${answerId}`,
        {
            isCompleted: isCompleted,
            questionAnswers: [questionAnswer]
        },
        getBaseOptions()
    );
}

/**
 * Get all questions for a given survey
 * @returns
 */
export function getAllQuestions() {
    return get(
        `${getBaseUrl()}/questions`,
        getBaseOptions()
    );
}

/**
 * get the answer object, will contain array of question answers
 *
 * @export
 * @param {Number} surveyId an integer
 * @return {*}
 */
export function getAnswer(answerId) {
    return get(
        `${getBaseUrl()}/answers/${answerId}`,
        getBaseOptions()
    );
}

/**
 * get the survey object
 *
 * @export
 * @return {*}
 */
export function getSurvey() {
    return get(
        `${getBaseUrl()}`,
        getBaseOptions()
    );
}

export function getResults() {
    return get(
        `${getBaseUrl()}/answers/results`,
        getBaseOptions()
    );
}

export function getQrCode(url) {
    return new Promise((resolve, reject) => {
        fetch(`${getBaseUrl()}/qrCode?data=${encodeURIComponent(url)}&size=200`, fetchOptions('GET', undefined, getBaseOptions()))
            .then(async (response) => {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);

                return { url, blob };
            })
            .then(resolve)
            .catch(reject);
    });
}

export function getResultsAsFile() {
    return new Promise((resolve, reject) => {
        fetch(`${getBaseUrl()}/answers/results/export?format=xlsx`, fetchOptions('GET', undefined, getBaseOptions()))
            .then(async (response) => {
                const filename = response.headers.get('Content-Disposition').match(/filename=(.+);/)[1];

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);

                return { filename, url };
            })
            .then(resolve)
            .catch(reject);
    });
}

/**
 * get respondents for a survey
 *
 * @export
 * @param {string} searhText a string to search for names. If null or empty, search is not performed
 * @param {string} respondentStatus a string of type 'Completed' or 'OnGoing'
 * @param {number} pageIndex an integer of the page index
 * @param {number} pageSize an integer of the page size
 * @return {*}
 */
export function getRespondents(searhText, pageIndex = 0, pageSize = 50) {
    return get(
        `${getBaseUrl()}/respondents?Search=${searhText}&PageIndex=${pageIndex}&PageSize=${pageSize}`,
        getBaseOptions()
    );
}

/**
 * Check if survey has answers
 *
 * @export
  * @return {*}
 */
export function hasRespondents() {
    return get(
        `${getBaseUrl()}/respondents/existence`,
        getBaseOptions()
    );
}

export function deleteRespondents(answerIds) {
    return del(
        `${getBaseUrl()}/respondents/responses?answerIds=${answerIds.join(',')}`,
        {},
        getBaseOptions()
    );
}

/**
 * This is a sample post that takes some data and the antiForgery information
 * @param {Object} data The data to be posted
 * @param {Object} antiForgery The antiForgery pulled from the store
 */
/*
 export const PostData = (data, antiForgery) =>
    postJson(endpoints.data, data, {
        headers: {
            [antiForgery.antiForgeryHeaderName]: antiForgery.antiForgeryHeaderValue
        }
    });
*/
